/**
 * List History Page
 * View and restore previously cleared shopping lists
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import { formatCurrency, formatDate } from '@/lib/utils';
import { itemsAPI } from '@/services/api';
import toast from 'react-hot-toast';

const ListHistoryPage = () => {
  const { logout } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [restoreModal, setRestoreModal] = useState({ open: false, entry: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, entry: null });

  /**
   * Fetch clear history
   */
  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await itemsAPI.getAllClearHistory();
      
      if (response.success && response.data) {
        setHistory(response.data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle restore list
   */
  const handleRestore = async () => {
    const { entry } = restoreModal;
    
    try {
      const response = await itemsAPI.restoreClearHistoryEntry(entry.historyId);
      
      if (response.success) {
        const { restoredCount, notFoundCount } = response.data;
        
        if (notFoundCount && notFoundCount > 0) {
          toast.success(`Restored ${restoredCount} items. ${notFoundCount} items were not found.`, {
            duration: 5000
          });
        } else {
          toast.success(`Restored ${restoredCount} items!`);
        }
        
        // Refresh history
        await fetchHistory();
        setRestoreModal({ open: false, entry: null });
      }
    } catch (error) {
      console.error('Error restoring list:', error);
      
      // Show more detailed error message
      const errorMsg = error?.response?.data?.error?.message 
        || error?.response?.data?.message 
        || error.message 
        || 'Failed to restore list';
      
      toast.error(errorMsg, { duration: 5000 });
    }
  };

  /**
   * Handle delete history entry
   */
  const handleDelete = async () => {
    const { entry } = deleteModal;
    
    try {
      const response = await itemsAPI.deleteClearHistoryEntry(entry.historyId);
      
      if (response.success) {
        toast.success('History entry deleted');
        // Remove from local state
        setHistory(prev => prev.filter(h => h.historyId !== entry.historyId));
        setDeleteModal({ open: false, entry: null });
      }
    } catch (error) {
      console.error('Error deleting history:', error);
      toast.error('Failed to delete history entry');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <LoadingSpinner size="lg" text="Loading history..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground pb-20">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">
            Deleted Lists
          </h1>
          <p className="text-muted-foreground">
            View and restore your previously cleared shopping lists
          </p>
        </div>

        {/* History List */}
        {history.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-muted-foreground mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg text-muted-foreground mb-4">No deleted lists yet</p>
              <Link to="/shopping-list">
                <Button>Go to Shopping List</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {history.map((entry) => (
              <Card key={entry.historyId} className={entry.isRestored ? 'opacity-60' : ''}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-foreground">
                          {entry.listName}
                        </h3>
                        {entry.isRestored && (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            Restored
                          </span>
                        )}
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          entry.type === 'all' 
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}>
                          {entry.type === 'all' ? 'Full List' : 'Purchased Items'}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
                        <span>📦 {entry.deletedCount} items</span>
                        <span>•</span>
                        <span>💰 {formatCurrency(entry.totalSpent || 0)}</span>
                        <span>•</span>
                        <span>🗓️ {formatDate(entry.clearedAt)}</span>
                        {entry.restoredAt && (
                          <>
                            <span>•</span>
                            <span>♻️ Restored {formatDate(entry.restoredAt)}</span>
                          </>
                        )}
                      </div>

                      {/* Item List Preview */}
                      {entry.items && entry.items.length > 0 && (
                        <div className="mt-3">
                          <button
                            onClick={() => setSelectedHistory(
                              selectedHistory?.historyId === entry.historyId ? null : entry
                            )}
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {selectedHistory?.historyId === entry.historyId ? 'Hide' : 'Show'} items ({entry.items.length})
                          </button>
                          
                          {selectedHistory?.historyId === entry.historyId && (
                            <div className="mt-3 p-4 bg-muted rounded-lg">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {entry.items.slice(0, 20).map((item, idx) => (
                                  <div key={idx} className="flex justify-between text-sm">
                                    <span className="text-foreground">
                                      {item.name} {item.quantity > 1 && `(×${item.quantity})`}
                                    </span>
                                    {item.price && (
                                      <span className="text-muted-foreground">
                                        {formatCurrency(item.price * item.quantity)}
                                      </span>
                                    )}
                                  </div>
                                ))}
                                {entry.items.length > 20 && (
                                  <div className="text-sm text-muted-foreground">
                                    ... and {entry.items.length - 20} more items
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 ml-4">
                      {!entry.isRestored && (
                        <Button
                          size="sm"
                          onClick={() => setRestoreModal({ open: true, entry })}
                          className="whitespace-nowrap"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                          </svg>
                          Restore
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleteModal({ open: true, entry })}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Restore Modal */}
      <Modal
        open={restoreModal.open}
        title="Restore Shopping List?"
        onClose={() => setRestoreModal({ open: false, entry: null })}
        onConfirm={handleRestore}
        confirmText="Restore"
      >
        {restoreModal.entry && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  Restore {restoreModal.entry.deletedCount} items
                </h4>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  This will bring back all items from "{restoreModal.entry.listName}" to your active shopping list.
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Items will be marked as not deleted and will appear in your shopping list again.
            </p>
          </div>
        )}
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={deleteModal.open}
        title="Delete History Entry?"
        onClose={() => setDeleteModal({ open: false, entry: null })}
        onConfirm={handleDelete}
        confirmText="Delete"
        variant="danger"
      >
        {deleteModal.entry && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="text-sm font-semibold text-red-900 dark:text-red-100 mb-1">
                  Permanently delete history entry
                </h4>
                <p className="text-sm text-red-800 dark:text-red-200">
                  This will remove the history entry for "{deleteModal.entry.listName}". The actual items remain in your database for statistics.
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Note: Deleting this history entry only removes the record. Your purchase statistics will be preserved.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ListHistoryPage;
